/**
 * ContactVerificationForm — the contact-details card (US4).
 *
 * The proto (`UpdateContactsModal`) edits INLINE: a single rounded card whose
 * rows flip between a read-only value and an `Input` in place. This component is
 * that card — the ordered list of `ContactFieldRow`s (street, postalCode, city,
 * email, phone) with a divider between rows (not after the last). It is rendered
 * by `ContactVerificationPrompt` in BOTH choice (read) and editing modes; the
 * `editing` flag and the edit-form handles drive each row.
 *
 * The earlier separate "edit form" split collapsed when the layout went inline:
 * the `<form>` element, the submit pipeline, and the sticky action bar now live
 * on the prompt (the action bar is sticky-positioned OUTSIDE the scrollable
 * content column, so it cannot live inside this card). Validation, postal
 * autofill, and email autocomplete live in `useContactEditForm`. This file is
 * purely the card's structure + per-field config.
 *
 * Token-backed styling only: card surface `bg-card`, dividers `border-border`.
 *
 * Owned by feature 026-proto-convergence-v2.
 * Task: T041
 */

import type { ComponentType, JSX } from 'react';
import type { MessageKeys } from '../i18n/messages/index.js';
import { useTranslate } from '../i18n/useTranslate.js';
import {
  BuildingIcon,
  EnvelopeIcon,
  type IconProps,
  MailboxIcon,
  MapPinIcon,
  PhoneIcon,
} from '../shell/icons.js';
import { ContactFieldRow } from './ContactFieldRow.js';
import type { ContactEditForm, FieldName } from './useContactEditForm.js';
import { FIELD_ORDER } from './useContactEditForm.js';

interface FieldConfig {
  readonly icon: ComponentType<IconProps>;
  readonly labelKey: MessageKeys;
  readonly inputType: string;
  readonly autoComplete: string;
}

/**
 * Per-field icon + label + edit-input metadata, in the proto's row order
 * (street, postalCode, city, email, phone). The icons are the hand-authored
 * shell set (no lucide — D-043); the order is `FIELD_ORDER`.
 */
const FIELD_CONFIG = {
  street: {
    icon: MapPinIcon,
    labelKey: 'profile.verify.field.street',
    inputType: 'text',
    autoComplete: 'street-address',
  },
  postalCode: {
    icon: MailboxIcon,
    labelKey: 'profile.verify.field.postalCode',
    inputType: 'text',
    autoComplete: 'postal-code',
  },
  city: {
    icon: BuildingIcon,
    labelKey: 'profile.verify.field.city',
    inputType: 'text',
    autoComplete: 'address-level2',
  },
  email: {
    icon: EnvelopeIcon,
    labelKey: 'profile.verify.field.email',
    inputType: 'email',
    autoComplete: 'email',
  },
  phone: {
    icon: PhoneIcon,
    labelKey: 'profile.verify.field.phone',
    inputType: 'tel',
    autoComplete: 'tel',
  },
} as const satisfies Record<FieldName, FieldConfig>;

export interface ContactVerificationFormProps {
  /** Whether the card is in inline-edit mode (rows render `Input`s). */
  readonly editing: boolean;
  /** Edit-mode state + handlers from `useContactEditForm`. */
  readonly editForm: ContactEditForm;
}

export function ContactVerificationForm({
  editing,
  editForm,
}: ContactVerificationFormProps): JSX.Element {
  const t = useTranslate();

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      {FIELD_ORDER.map((field, index) => {
        const config = FIELD_CONFIG[field];
        return (
          <div key={field}>
            <ContactFieldRow
              field={field}
              icon={config.icon}
              label={t(config.labelKey)}
              inputType={config.inputType}
              autoComplete={config.autoComplete}
              editing={editing}
              value={editForm.values[field]}
              errorKey={editForm.errors[field]}
              fieldIdPrefix={editForm.fieldIdPrefix}
              onChange={editForm.setFieldValue}
              emailSuggestions={field === 'email' ? editForm.emailSuggestions : undefined}
              onSelectSuggestion={field === 'email' ? editForm.selectEmailSuggestion : undefined}
              onDismissSuggestions={
                field === 'email' ? editForm.dismissEmailSuggestions : undefined
              }
            />
            {index < FIELD_ORDER.length - 1 ? (
              <div className="mx-6 border-t border-border" aria-hidden="true" />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
